import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { DropdownItemNumber } from 'src/app/sharedFeatures/models/Dropdown-item';
import { LookUpService } from 'src/app/sharedFeatures/services/lookUp.service';
import { NotificationService } from 'src/app/sharedFeatures/services/notification.service';

@Component({
  selector: 'dct-task-tree-selection',
  templateUrl: './task-tree-selection.component.html',
  styleUrls: ['./task-tree-selection.component.scss'],
})
export class TaskTreeSelectionComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();

  nodes: any[] = []; // Data for p-treeSelect component
  selectedMilestoneId!: number | undefined;
  loading: boolean = false; // Loading indicator for the whole component
  expandedNodes: Set<number> = new Set(); // Track expanded nodes to prevent unwanted collapsing
  selectionResult: any = {};

  @Output() selectionChange = new EventEmitter<{
    sectionId: number;
    projectId: number;
    milestoneId: number;
  }>();

  constructor(
    private lookUpService: LookUpService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadSections();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadSections(): void {
    this.loading = true;
    this.subscriptions.add(
      this.lookUpService.sectionSearchLookup().subscribe({
        next: res => {
          this.nodes = (res.collection || []).map(
            (section: DropdownItemNumber) => ({
              label: section.name,
              data: { id: section.id, type: 'section' },
              leaf: false,
              expanded: this.expandedNodes.has(section.id),
            })
          );
          this.loading = false;
        },
        error: err => {
          this.loading = false;
          this.showError(err.message);
        },
      })
    );
  }

  public reset(): void {
    this.selectedMilestoneId = undefined;
    this.expandedNodes.clear();
    this.selectionResult = {};
    this.loadSections();
  }

  onNodeExpand(event: any): void {
    const node = event.node;
    if (node && node.data.type === 'section') {
      this.loadProjects(node);
    } else if (node && node.data.type === 'project') {
      this.loadMilestones(node);
    }
  }

  loadProjects(sectionNode: any): void {
    this.loading = true;
    const sectionId = sectionNode.data.id;
    this.expandedNodes.add(sectionId);

    this.subscriptions.add(
      this.lookUpService.projectsSearchLookup(sectionId).subscribe({
        next: res => {
          sectionNode.children =
            (res.collection || []).map((project: DropdownItemNumber) => ({
              label: project.name,
              data: { id: project.id, type: 'project' },
              leaf: false,
              expanded: this.expandedNodes.has(project.id),
            })) || [];
          this.updateNodes();
          this.loading = false;
        },
        error: err => {
          this.loading = false;
          this.showError(err.message);
        },
      })
    );
  }

  loadMilestones(projectNode: any): void {
    this.loading = true;
    const projectId = projectNode.data.id;
    this.expandedNodes.add(projectId);

    this.subscriptions.add(
      this.lookUpService.milstonsSearchLookup(projectId).subscribe({
        next: res => {
          projectNode.children =
            (res.collection || []).map((milestone: DropdownItemNumber) => ({
              label: milestone.name,
              data: { id: milestone.id, type: 'milestone' },
              leaf: true,
            })) || [];
          this.updateNodes();
          this.loading = false;
        },
        error: err => {
          this.loading = false;
          this.showError(err.message);
        },
      })
    );
  }

  onNodeSelect(event: any): void {
    const selectedNode = event.node;
    if (selectedNode && selectedNode.data.type === 'milestone') {
      const sectionNode = event.node.parent.parent;
      const projectNode = event.node.parent;

      this.selectionResult = {
        section: {
          id: sectionNode.data.id,
          name: sectionNode.label,
        },
        project: {
          id: projectNode.data.id,
          name: projectNode.label,
        },
        milestone: {
          id: selectedNode.data.id,
          name: selectedNode.label,
        },
      };
      this.selectionChange.emit(this.selectionResult);
    }
  }

  private updateNodes(): void {
    this.nodes = [...this.nodes]; // Reassign nodes to trigger change detection
  }

  private showError(message: string): void {
    this.notificationService.showError(message, 'Error');
  }
}
